import { useEffect, useMemo } from "react";
import { useImmer } from "use-immer";
import {
    IApplicantInfo,
    IQuestionDataResponse,
    ICaseProduct
} from "../interfaces/IAppFormData";

interface Insurer {
    id: number;
    name: string;
}

export interface IAppFormStateProps {
    quoteGroupId: string;
    digitalAdviceSessionUserID: string;
    applicationToken: string;
    insurer: Insurer;
    applicationId: number;
    applicants: IApplicantInfo[];
    response: IQuestionDataResponse;
    formData: Array<any>;
    productId: number;
    caseProducts: Array<ICaseProduct>;
    sectionContent: object;
}

export interface IAppFormSelectors {
    getQuoteGroupId?: () => string;
    getDASessionUserId?: () => string;
    getApplicationToken?: () => string;
    getInsurerName?: () => string;
    getInsurerId?: () => number;
    getApplicantList?: () => Array<IApplicantInfo>;
    getEachApplicantInfo?: (params: string) => IApplicantInfo;
    getApplicantName: (applicantToken: string) => string;
    getApplicationId: () => number;
    getQuestionResponse: () => IQuestionDataResponse;
    getFormData: () => Array<any>;
    getEachFormData: (id: number) => any;
    getCaseProducts: () => Array<ICaseProduct>;
    getSectionContent: () => object;
}

export interface IAppFormActions {
    reset: () => Promise<void>;
    setQuoteGroupId?: (quoteGroupId: string) => void;
    setDASessionUserId?: (digitalAdviceSessionUserID: string) => void;
    setApplicationToken: (applicationToken: string) => void;
    setInsurer: (insurer: Insurer) => void;
    setApplicantList: (list: Array<IApplicantInfo>) => void;
    setApplicationId: (applicationId: number) => void;
    setQuestionResponse: (response: IQuestionDataResponse) => void;
    resetFormData: () => void;
    setFormData: (data: any) => void;
    setCaseProducts: (cases: Array<ICaseProduct>) => void;
    setSectionContent: (content: object) => void;
}

interface IAppFormState {
    selectors: IAppFormSelectors;
    actions: IAppFormActions;
}

const useAppFormState = (defaultState: IAppFormStateProps): IAppFormState => {
    const [state, setState] = useImmer<IAppFormStateProps>(defaultState);

    useEffect(() => {
        const stringState = JSON.stringify(state);
        window.localStorage.setItem("appform_states", stringState);
    }, [state]);

    return {
        selectors: {
            getQuoteGroupId: (): string => {
                return state.quoteGroupId;
            },
            getDASessionUserId: (): string => {
                return state.digitalAdviceSessionUserID;
            },
            getApplicationToken: (): string => {
                return state.applicationToken;
            },
            getInsurerName: (): string => {
                return state.insurer?.name;
            },
            getInsurerId: (): number => {
                return state.insurer?.id;
            },
            getApplicantList: (): Array<IApplicantInfo> => {
                return state.applicants;
            },
            getEachApplicantInfo: (params): IApplicantInfo => {
                const match = /^applicant([\d]+)$/gi.exec(params);
                const clientNumber = match? parseInt(match[1]) : 0;
                return state.applicants.find((i) => i.clientNumber === clientNumber);
            },
            getApplicantName: (applicantToken: string): string => {
                const isMoreApplicant = state.applicants?.length > 1;
                return isMoreApplicant
                    ? state.applicants.find(
                          (i) => i?.applicantToken === applicantToken
                      )?.firstName || ""
                    : "";
            },
            getApplicationId: (): number => {
                return state.applicationId || 0;
            },
            getQuestionResponse: (): IQuestionDataResponse => {
                return state.response;
            },
            getFormData: (): Array<any> => {
                return state.formData;
            },
            getEachFormData: (id: number): any => {
                return state.formData.find((_i) => _i.id === id)?.data;
            },
            getCaseProducts: (): Array<ICaseProduct> => {
                return state.caseProducts;
            },
            getSectionContent: (): object => {
                return state.sectionContent;
            }
        },
        actions: useMemo(
            () => ({
                reset: async (): Promise<void> => {
                    window.localStorage.removeItem("appform_states");
                    return setState((store) => {
                        store.quoteGroupId = "";
                        store.digitalAdviceSessionUserID = "";
                        store.applicationToken = "";
                        store.insurer = null;
                        store.applicationId = 0;
                        store.applicants = [];
                        store.formData = [];
                        store.caseProducts = [];
                        store.sectionContent = null;
                    });
                },
                setQuoteGroupId: (quoteGroupId: string): void => {
                    if (!quoteGroupId) return;
                    setState((advice) => {
                        advice.quoteGroupId = quoteGroupId;
                    });
                },
                setDASessionUserId: (digitalAdviceSessionUserID: string): void => {
                    if (!digitalAdviceSessionUserID) return;
                    setState((advice) => {
                        advice.digitalAdviceSessionUserID = digitalAdviceSessionUserID;
                    });
                },
                setApplicationToken: (applicationToken: string): void => {
                    if (!applicationToken) return;
                    setState((advice) => {
                        advice.applicationToken = applicationToken;
                    });
                },
                setInsurer: (insurer: Insurer): void => {
                    if (!insurer || !insurer?.id) return;
                    setState((advice) => {
                        advice.insurer = insurer;
                    });
                },
                setApplicantList: (list: Array<IApplicantInfo>): void => {
                    if (!list?.length) return;
                    setState((advice) => {
                        advice.applicants = list;
                    });
                },
                setApplicationId: (applicationId: number): void => {
                    setState((advice) => {
                        advice.applicationId = applicationId;
                    });
                },
                setQuestionResponse: (response: IQuestionDataResponse): void => {
                    setState((advice) => {
                        advice.response = response;
                    });
                },
                resetFormData: (): void => {
                    setState((form) => {
                        form.formData = [];
                    });
                },
                setFormData: (data: any): void => {
                    setState((form) => {
                        form.formData = [...form.formData, data];
                    });
                },
                setCaseProducts: (caseProducts: Array<ICaseProduct>): void => {
                    setState((advice) => {
                        advice.caseProducts = caseProducts;
                    });
                },
                setSectionContent: (content: object): void => {
                    setState((store) => {
                        store.sectionContent = content;
                    });
                }
            }),
            [setState]
        )
    };
};

export default useAppFormState;
